// Global Instructions Rule Applied!
//
// Configuration → Waterfall Templates: the shapes a fund's distribution
// waterfall is created from (cfg.waterfall_templates / _tiers / _splits;
// docs/WATERFALL_CARRY_DESIGN.md). Templates are by shape, never per fund:
// every rate and multiple comes from the LP's commitment terms or the fund's
// Carried Interest defaults when the waterfall runs, so a template names the
// SOURCE of each figure ("the commitment base carry rate"), not the figure.
//
// A fund waterfall created from a template is a COPY, not a live link:
// editing or deleting a template never reaches back into the funds already
// created from it (wf__create_from_template copies the tiers).
//
// Master-detail, following ChartTemplatesPanel: the list is replaced by the
// editor when a template is opened. The editor's tier grid is the fund Terms
// tab's (WaterfallTierGrid), so the two cannot drift. After a save or a
// delete the global configs are refreshed, so the Terms tab's "Use a
// template" list shows the change without a reload.

import React, { useCallback, useEffect, useState } from 'react'
import { Button, Input, InputNumber, Popconfirm, Select, Table, Tooltip, Typography } from 'antd'
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import HelpTip from '../../../core/components/HelpTip.tsx'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { decimalToPercent, percentToDecimal } from '../../../core/utils/inputNumberFormat.ts'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import type {
  WaterfallTemplateRecord,
  WaterfallTemplateSavePayload
} from '../../../state_manager/data_models/waterfall_template_record.ts'
import WaterfallTierGrid, {
  blankTierRow,
  firstIncompleteTier,
  rowsToTierPayloads,
  tierToRow,
  useWaterfallLookups,
  type TierRow
} from '../../funds/components/forms/tabs/WaterfallTierGrid.tsx'
import {
  deleteWaterfallTemplate,
  fetchWaterfallTemplates,
  saveWaterfallTemplate
} from '../services/waterfallTemplatesConfigService.ts'
import { refreshBoardConfigs } from '../services/refreshBoardConfigs.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

const { Title, Text } = Typography

interface EditorState {
  id: string | null
  name: string
  description: string
  basis_id: number | null
  scope_id: number | null
  history_check_id: number | null
  recallable_pct: number | null
  rows: TierRow[]
}

export default function WaterfallTemplatesPanel() {
  const lk = useWaterfallLookups()
  const [templates, setTemplates] = useState<WaterfallTemplateRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchWaterfallTemplates(token())
      if (res.error) showErrorToast(res.error)
      setTemplates(res.data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const openEditor = useCallback(
    (tpl: WaterfallTemplateRecord | null) => {
      if (!tpl) {
        setEditor({
          id: null,
          name: '',
          description: '',
          basis_id: lk.bases[0]?.id ?? null,
          scope_id: lk.scopes[0]?.id ?? null,
          history_check_id: lk.defaultHistoryCheckId,
          recallable_pct: null,
          rows: []
        })
        return
      }
      setEditor({
        id: tpl.id,
        name: tpl.name,
        description: tpl.description ?? '',
        basis_id: tpl.basis_id ?? null,
        scope_id: tpl.scope_id ?? null,
        history_check_id: tpl.history_check_id ?? lk.defaultHistoryCheckId,
        recallable_pct: tpl.recallable_pct ?? null,
        rows: tpl.tiers.map((t) => tierToRow(t, lk.gpClassId, lk.lpClassId))
      })
    },
    [lk]
  )

  const patch = (p: Partial<EditorState>) => setEditor((prev) => (prev ? { ...prev, ...p } : prev))

  const handleDelete = useCallback(
    async (templateId: string) => {
      setDeletingId(templateId)
      try {
        const res = await deleteWaterfallTemplate(templateId, token())
        if (res.error) {
          showErrorToast(res.error)
          return
        }
        toast.success(res.data?.status_message || 'Template deleted')
        refreshBoardConfigs()
        await load()
      } finally {
        setDeletingId(null)
      }
    },
    [load]
  )

  const handleSave = useCallback(async () => {
    if (!editor) return
    if (!editor.name.trim() || editor.basis_id == null || editor.scope_id == null) {
      showErrorToast('The template needs a name, a basis and a scope')
      return
    }
    if (editor.rows.length === 0) {
      showErrorToast('Add at least one tier')
      return
    }
    if (lk.gpClassId == null || lk.lpClassId == null || lk.remainderSourceId == null) {
      showErrorToast('The waterfall lookups are not loaded - refresh and try again')
      return
    }
    const invalid = firstIncompleteTier(editor.rows)
    if (invalid > 0) {
      showErrorToast(`Tier ${invalid} needs a type and a stop metric (None for the last tier)`)
      return
    }
    setSaving(true)
    try {
      const payload: WaterfallTemplateSavePayload = {
        ...(editor.id ? { id: editor.id } : {}),
        name: editor.name.trim(),
        description: editor.description.trim() || null,
        basis_id: editor.basis_id,
        scope_id: editor.scope_id,
        history_check_id: editor.history_check_id,
        recallable_pct: editor.recallable_pct,
        tiers: rowsToTierPayloads(editor.rows, lk, false)
      }
      const res = await saveWaterfallTemplate(payload, token())
      if (res.error) {
        // a duplicate name or a tier rule comes back as the RPC's sentence -
        // the editor stays open with the work intact
        showErrorToast(res.error)
        return
      }
      toast.success('Template saved')
      setEditor(null)
      refreshBoardConfigs()
      await load()
    } finally {
      setSaving(false)
    }
  }, [editor, lk, load])

  const labelOf = (list: Array<{ id: number; label: string }>, id: number | null | undefined) =>
    list.find((x) => x.id === id)?.label ?? '—'

  const listColumns: ColumnsType<WaterfallTemplateRecord> = [
    { title: 'Template', dataIndex: 'name' },
    { title: 'Basis', dataIndex: 'basis_id', width: 120, render: (v: number | null) => labelOf(lk.bases, v) },
    { title: 'Scope', dataIndex: 'scope_id', width: 130, render: (v: number | null) => labelOf(lk.scopes, v) },
    {
      title: 'Earlier distributions',
      dataIndex: 'history_check_id',
      width: 160,
      render: (v: number | null) => labelOf(lk.historyChecks, v ?? lk.defaultHistoryCheckId)
    },
    { title: 'Tiers', width: 70, align: 'right', render: (_: unknown, row) => row.tiers.length },
    {
      title: (
        <span className='whitespace-nowrap'>
          Used by
          <HelpTip title='Fund waterfalls created from this template. They are copies: editing or deleting the template leaves them as they are.' />
        </span>
      ),
      dataIndex: 'prog__fund_count',
      width: 100,
      align: 'right'
    },
    {
      title: '',
      width: 100,
      align: 'center',
      render: (_: unknown, row) => (
        <div className='flex items-center justify-center gap-1'>
          <Button type='text' size='small' icon={<EditOutlined />} aria-label={`Edit ${row.name}`} onClick={() => openEditor(row)} />
          <Popconfirm
            title='Delete this template?'
            description='Fund waterfalls already created from it are unaffected.'
            okText='Delete'
            okButtonProps={{ danger: true, loading: deletingId === row.id }}
            onConfirm={() => void handleDelete(row.id)}
          >
            <Button type='text' size='small' danger icon={<DeleteOutlined />} aria-label={`Delete ${row.name}`} />
          </Popconfirm>
        </div>
      )
    }
  ]

  if (editor) {
    return (
      <div className={CONFIG_PANEL_SCROLL_CLASS}>
        <div className='flex items-center justify-between mb-3'>
          <div className='flex items-center gap-2'>
            <Button size='small' icon={<ArrowLeftOutlined />} onClick={() => setEditor(null)}>
              Back
            </Button>
            <Title level={5} style={{ margin: 0 }}>
              {editor.id ? 'Edit template' : 'New template'}
            </Title>
          </div>
          <div className='flex items-center gap-2'>
            <Button size='small' icon={<PlusOutlined />} onClick={() => patch({ rows: [...editor.rows, blankTierRow(lk.lpClassId)] })}>
              Add tier
            </Button>
            <Button type='primary' size='small' icon={<SaveOutlined />} loading={saving} onClick={() => void handleSave()}>
              Save Template
            </Button>
          </div>
        </div>

        <div className='grid grid-cols-2 xl:grid-cols-4 gap-x-3 gap-y-2 mb-4'>
          <div className='xl:col-span-2'>
            <Text type='secondary'>Name</Text>
            <Input
              size='small'
              value={editor.name}
              maxLength={120}
              placeholder='Template name'
              aria-label='Template name'
              onChange={(e) => patch({ name: e.target.value })}
            />
          </div>
          <div>
            <Text type='secondary'>
              <Tooltip title='Fund: the stop tests run on the LPs as a group (Origin §6.4(a)). Commitment: each LP is tested on its own contributions and distributions (SBIC, Origin 21, the SPVs).'>
                Basis
              </Tooltip>
            </Text>
            <Select
              size='small'
              className='w-full'
              value={editor.basis_id}
              options={lk.basisOptions}
              placeholder='Basis...'
              aria-label='Template basis'
              onChange={(v) => patch({ basis_id: v })}
            />
          </div>
          <div>
            <Text type='secondary'>
              <Tooltip title='How proceeds are apportioned before the tiers run: by commitment (Whole Fund) or by each investor&apos;s share of the investment realized (Deal-by-Deal, Origin 21).'>
                Scope
              </Tooltip>
            </Text>
            <Select
              size='small'
              className='w-full'
              value={editor.scope_id}
              options={lk.scopeOptions}
              placeholder='Scope...'
              aria-label='Template scope'
              onChange={(v) => patch({ scope_id: v })}
            />
          </div>
          <div className='xl:col-span-2'>
            <Text type='secondary'>
              Earlier distributions
              <HelpTip title={lk.historyCheckHelp} maxWidth={720} placement='right' />
            </Text>
            <Select
              size='small'
              className='w-full'
              value={editor.history_check_id ?? lk.defaultHistoryCheckId}
              options={lk.historyCheckOptions}
              placeholder='Check...'
              aria-label='Check of earlier distributions'
              onChange={(v) => patch({ history_check_id: v })}
            />
          </div>
          <div>
            <Text type='secondary'>
              <Tooltip title='Return of capital may be recalled up to this share of commitments (Origin §6.4(h)(ii), SBIC §7.01(e): 120%). Empty = not recallable.'>
                Recallable
              </Tooltip>
            </Text>
            <InputNumber
              size='small'
              className='w-full'
              value={decimalToPercent(editor.recallable_pct)}
              precision={0}
              suffix='%'
              aria-label='Recallable percent'
              onChange={(v) => patch({ recallable_pct: percentToDecimal(v) })}
            />
          </div>
          <div className='col-span-2 xl:col-span-4'>
            <Text type='secondary'>Description</Text>
            <Input.TextArea
              size='small'
              autoSize={{ minRows: 2, maxRows: 6 }}
              value={editor.description}
              placeholder='What the shape is and which agreements follow it - a waterfall created from the template starts with this as its notes'
              aria-label='Template description'
              onChange={(e) => patch({ description: e.target.value })}
            />
          </div>
        </div>

        <WaterfallTierGrid
          rows={editor.rows}
          onChange={(rows) => patch({ rows })}
          disabled={false}
          lookups={lk}
          footer={
            <Text type='secondary'>
              Rates and multiples that read &ldquo;commitment …&rdquo; are sources, not figures: when a fund runs the
              waterfall each LP supplies its own commitment terms, or the fund&apos;s Carried Interest defaults where it
              states none.
            </Text>
          }
        />
      </div>
    )
  }

  return (
    <div className={CONFIG_PANEL_SCROLL_CLASS}>
      <div className='flex items-center justify-between mb-3'>
        <Title level={5} style={{ margin: 0 }}>
          Waterfall Templates
        </Title>
        <div className='flex items-center gap-2'>
          <Button type='primary' size='small' icon={<PlusOutlined />} onClick={() => openEditor(null)}>
            New Template
          </Button>
          <Button size='small' icon={<ReloadOutlined />} onClick={() => void load()}>
            Refresh
          </Button>
        </div>
      </div>

      <Text type='secondary' className='block mb-2'>
        The shapes a fund&apos;s distribution waterfall is created from (the fund&apos;s Terms tab, &ldquo;Use a
        template&rdquo;). A template names the source of every rate and multiple - the LP&apos;s commitment terms or
        the fund&apos;s Carried Interest defaults - never a figure, so one shape serves every fund that follows it.
        Creating a waterfall copies the template; later edits do not affect funds already set up.
      </Text>

      <Table size='small' loading={loading} columns={listColumns} dataSource={templates} pagination={false} rowKey='id' />
    </div>
  )
}
